<template>
  <div class="c-search-filters flex flex-wrap items-center gap-2 w-full">
    <CSearchOnClickWithSuggestions
      v-if="!!pitcherSettings.enable_search_with_suggestions"
      ref="searchOnClickRef"
      :disabled="disabled"
      :initial-search-value="initialSearchValue"
      :placeholder="placeholder"
      :suggestions-fetcher="fetchSuggestions"
      @search="emit('update:search', $event, filters)"
    />
    <CSearchOnClick
      v-else
      ref="searchOnClickRef"
      :disabled="disabled"
      :initial-search-value="initialSearchValue"
      :placeholder="placeholder"
      @search="emit('update:search', $event, filters)"
    >
      <template v-if="searchInOptions" #options>
        <CSelect
          bordered
          :class="'w-[200px]'"
          :default-value="searchInDefaultValue?.value"
          :disabled="disabled"
          :options="searchInOptions"
          @update:value="emit('update:searchIn', $event)"
        />
      </template>
    </CSearchOnClick>
    <slot name="input-suffix" />

    <template v-if="!props.hideFilters">
      <!-- Only show outside filters when display_metadata_filters_in_modal is false -->
      <template v-if="!pitcherSettings.display_metadata_filters_in_modal">
        <!-- Dependent filters -->
        <template v-if="hasDependentFilters">
          <Component
            :is="componentMap[getComponentNameByFilterType(f.type)]"
            v-for="f in visibleDependentFields"
            :key="f.name"
            :allow-custom-date-range="f.type === 'date'"
            :disabled="disabled"
            :entity="entity"
            :external-filters="getExternalFiltersForField(f.type, f.name, isFieldFromDependentSection(f.name))"
            :filter-key="f.name"
            :include-draft-values="includeDraftValues"
            :is-dependent-field="isFieldFromDependentSection(f.name)"
            :is-section="isSection"
            :is-template="isTemplate"
            :modelValue="filters[f.name]"
            :multiple="f.type === 'multi-select'"
            :name="f.label || f.name"
            :numeric="f.type === 'number'"
            :options="
              f.type === 'checkbox'
                ? getFilterOptions(f.type, f.options, (f as any).available_values)
                : getFilterOptions(f.type, f.options)
            "
            :owner-id="ownerId"
            :system-filtered-ids="systemFilteredIds"
            :tags-not-in="tagsNotIn"
            @update:modelValue="(v: any) => onUpdateDependentField(f, v)"
          />
        </template>
        <!-- Original non-dependent filters -->
        <template v-else>
          <Component
            :is="componentMap[cc.componentName]"
            v-for="cc in computedCustomFilterComponents"
            :key="cc.name"
            :allow-custom-date-range="cc.type === 'date'"
            :disabled="disabled"
            :entity="entity"
            :external-filters="getExternalFiltersForField(cc.type, cc.name, isFieldFromDependentSection(cc.name))"
            :filter-key="cc.name"
            :include-draft-values="includeDraftValues"
            :is-custom="cc.isCustom"
            :is-dependent-field="isFieldFromDependentSection(cc.name)"
            :is-section="isSection"
            :is-template="isTemplate"
            :modelValue="filters[cc.name]"
            :name="cc.label || cc.name"
            :numeric="cc.type === 'number'"
            :options="cc.options ?? getFilterOptions(cc.type, cc.options)"
            :owner-id="ownerId"
            :system-filtered-ids="systemFilteredIds"
            :tags-not-in="tagsNotIn"
            @update:modelValue="(v: any) => onUpdateModelValue(cc, v)"
          />
          <Component
            :is="componentMap[f.componentName]"
            v-for="f in computedFilters"
            :key="f.name"
            :allow-custom-date-range="f.type === 'date'"
            :disabled="disabled"
            :entity="entity"
            :external-filters="getExternalFiltersForField(f.type, f.name, isFieldFromDependentSection(f.name))"
            :filter-key="f.name"
            :include-draft-values="includeDraftValues"
            :is-dependent-field="isFieldFromDependentSection(f.name)"
            :is-section="isSection"
            :is-template="isTemplate"
            :modelValue="filters[f.name]"
            :multiple="f.type === 'multi-select' ? true : undefined"
            :name="f.label || f.name"
            :numeric="f.type === 'number'"
            :options="getFilterOptions(f.type, f.options)"
            :owner-id="ownerId"
            :system-filtered-ids="systemFilteredIds"
            :tags-not-in="tagsNotIn"
            @update:modelValue="(v: any) => onUpdateModelValue(f, v)"
          />
        </template>
      </template>

      <!-- Popover for when display_metadata_filters_in_modal is true -->
      <CPopover
        v-if="pitcherSettings.display_metadata_filters_in_modal && hasAnyFilter"
        class="min-w-[500px] max-w-[700px] max-h-[400px] overflow-y-auto"
        placement="bottom"
        :show-arrow="false"
        trigger="click"
      >
        <template #trigger>
          <CButton icon-placement="left" quaternary>
            {{ t('canvasUI.common.filters') }}

            <div
              v-if="numberOfActiveFilters"
              class="ml-1 px-[6px] rounded h-[22px] flex items-center justify-center"
              :style="{ background: themeVars.primary, color: themeVars.base }"
            >
              {{ numberOfActiveFilters }}
            </div>
            <template #icon>
              <CIcon icon="filter" :size="16" />
            </template>
          </CButton>
        </template>
        <NCollapse :default-expanded-names="expandedNames">
          <div class="flex flex-col ma-2">
            <div v-if="hasAnySystemFilter" class="flex flex-wrap gap-2">
              <CCheckboxFilter
                v-for="f in systemFilters"
                :key="f.name"
                :disabled="disabled"
                :modelValue="filters[f.name]"
                :name="f.label || f.name"
                @update:modelValue="(v: any) => onUpdateModelValue(f, v)"
              />
            </div>
            <NCollapseItem
              v-for="(section, index) of sectionsWithAtLeastOneFilterableField"
              :key="index"
              class="flex flex-col gap-2"
              :name="section.label"
              :style="{ '--n-title-font-size': '16px' }"
              :title="section.label"
            >
              <Component
                :is="componentMap[getComponentNameByFilterType(f.type)]"
                v-for="f in section.are_fields_dependent ? visibleDependentFields : getSectionFieldsToRender(section)"
                :key="f.name"
                :allow-custom-date-range="f.type === 'date'"
                :disabled="disabled"
                :entity="entity"
                :external-filters="getExternalFiltersForField(f.type, f.name, isFieldFromDependentSection(f.name))"
                :filter-key="f.name"
                :include-draft-values="includeDraftValues"
                inline
                :is-dependent-field="isFieldFromDependentSection(f.name)"
                :is-section="isSection"
                :is-template="isTemplate"
                :modelValue="filters[f.name]"
                :multiple="f.type === 'multi-select' ? true : undefined"
                :name="f.label || f.name"
                :numeric="f.type === 'number'"
                :options="
                  f.type === 'checkbox'
                    ? getFilterOptions(f.type, f.options, (f as any).available_values)
                    : getFilterOptions(f.type, f.options)
                "
                :owner-id="ownerId"
                :system-filtered-ids="systemFilteredIds"
                :tags-not-in="tagsNotIn"
                @update:modelValue="
                  (v: any) => (section.are_fields_dependent ? onUpdateDependentField(f, v) : onUpdateModelValue(f, v))
                "
              />
            </NCollapseItem>
          </div>
        </NCollapse>
      </CPopover>

      <!-- Clear filters button -->
      <CButton
        v-if="hasActiveFilters"
        class="pa-1 pl-2 !normal-case !tracking-normal underline"
        :disabled="disabled"
        size="tiny"
        :style="{ borderLeft: `1px solid ${themeVars.primary5}` }"
        text
        @click="clearAllFiltersAndQuickFilters"
      >
        {{ t('canvasUI.components.canvases.CCanvasMetadataFilters.clearFilters') }}
      </CButton>
    </template>
  </div>
</template>
<script setup lang="ts">
import { omitBy, cloneDeep } from 'lodash-es'
import { NCollapse, NCollapseItem } from 'naive-ui'
import { computed, inject, onMounted, ref, type ComputedRef, type Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AxiosInstance } from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import CCheckboxFilter from '@lib/components/filters/CCheckboxFilter.vue'
import CDateRangeFilter from '@lib/components/filters/CDateRangeFilter.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CPopover from '@lib/components/CPopover/CPopover.vue'
import CSearchFilter from '@lib/components/filters/CSearchFilter.vue'
import CSearchOnClick from '@lib/components/CSearch/CSearchOnClick.vue'
import CSearchOnClickWithSuggestions from '@lib/components/CSearch/CSearchOnClickWithSuggestions.vue'
import CSelectFilter from '@lib/components/filters/CSelectFilter.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import useMetadataFilters from '@lib/composables/useMetadataFilters'
import useMetadataSearch from '@lib/composables/useMetadataSearch'
import useThemeVars from '@lib/composables/useThemeVars'
import type { PitcherInfo } from '@lib/types/app'
import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import { type MetadataTemplateField, type PaginatedMetadataTemplateList, MetadataTemplateFieldTypeEnum } from '@openapi'
import type { MetadataTemplateFieldWithComponentName } from '@lib/types/filter'
import { SystemFilter } from '@lib/types/sectionPopularity'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { SearchInOption } from './CSearchFilters.types'
import type { MetadataFilterRequest } from '@lib/types/core/MetadataFilter'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    engagingIds?: string[]
    entity: MetadataFilterRequest['entity']
    includeDraftValues?: boolean
    isSection?: MetadataFilterRequest['is_section']
    isTemplate?: MetadataFilterRequest['is_template']
    searchInOptions?: SearchInOption[]
    filters?: Record<string, any>
    initialSearchValue?: string
    ownerId?: number | null
    placeholder?: string
    popularIds?: string[]
    recommendedIds?: string[]
    showLatestFilter?: boolean
    hideMetadataFilters?: boolean
    hideFilters?: boolean
    customFilterComponents?: {
      type: MetadataTemplateFieldTypeEnum
      name: string
      label: string
      options?: { label: string; value: string }[]
    }[]
    enableDependentFilters?: boolean
    dependentFiltersEntity?: 'file' | 'canvas'
    contentType?: string[]
    tagsNotIn?: string[]
  }>(),
  {
    engagingIds: () => [],
    filters: () => ({}),
    includeDraftValues: false,
    initialSearchValue: '',
    placeholder: '',
    popularIds: () => [],
    recommendedIds: () => [],
    showLatestFilter: false,
    hideMetadataFilters: false,
    hideFilters: false,
    dependentFiltersEntity: 'file',
    tagsNotIn: () => [],
  },
)
const searchOnClickRef = ref<InstanceType<typeof CSearchOnClick> | null>(null)

const emit = defineEmits<{
  'update:filters': [filters: Record<string, any>]
  'update:search': [search: string, filters?: Record<string, any>]
  'update:searchIn': [searchIn: string]
}>()

defineExpose({
  clearSearch,
})

const { t } = useI18n()
const { getComponentNameByFilterType, getFilterOptions } = useMetadataFilters(t)
const themeVars = useThemeVars()

const { handleFetch } = useMetadataSearch({
  includeSearchTermAsOption: true,
})

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios', ref())

const expandedNames = ref<string[]>([])

const componentMap = {
  CCheckboxFilter,
  CSearchFilter,
  CSelectFilter,
  CDateRangeFilter,
} as Record<string, any>

const template = ref<Required<PaginatedMetadataTemplateList>['results'][number]>()
const originalTemplate = ref<Required<PaginatedMetadataTemplateList>['results'][number]>()

const hasAnySystemFilter = computed(
  () => props.popularIds?.length || props.engagingIds?.length || props.recommendedIds?.length || props.showLatestFilter,
)

const hasAnyFilter = computed(() => hasAnySystemFilter.value || !!fields.value?.length)
const hasDependentFilters = computed(
  () => props.enableDependentFilters && template.value?.sections?.some((s) => s.are_fields_dependent),
)
const searchInDefaultValue = computed(
  () => props.searchInOptions?.find((o: SearchInOption) => o.default) || props.searchInOptions?.[0],
)

const systemFilters = computed(() => {
  const f: MetadataTemplateFieldWithComponentName[] = []

  if (props.popularIds?.length)
    f.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.popular'),
      name: SystemFilter.POPULAR,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.engagingIds?.length)
    f.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.engaging'),
      name: SystemFilter.ENGAGING,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.recommendedIds?.length)
    f.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.recommended'),
      name: SystemFilter.RECOMMENDED,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.showLatestFilter)
    f.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.latest'),
      name: SystemFilter.LATEST,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  return f
})

const sectionsWithAtLeastOneFilterableField = computed(() => {
  return template.value?.sections?.filter((s) => s.fields.some((f) => f.is_filterable)) ?? []
})

const fields = computed(() => {
  return sectionsWithAtLeastOneFilterableField.value.flatMap((s) => s.fields)
})

const computedCustomFilterComponents = computed(() => {
  return (
    props.customFilterComponents?.map((cc) => ({
      ...cc,
      isCustom: true,
      componentName: getComponentNameByFilterType(cc.type),
    })) ?? []
  )
})

const computedFilters = computed(() => {
  if (props.hideMetadataFilters) return []
  const metadataFilters = fields.value.reduce((acc, { label, name, type, help_text, options, is_filterable }) => {
    if (!is_filterable) return acc

    const componentName = getComponentNameByFilterType(type)

    return [
      ...acc,
      {
        label,
        name,
        type,
        help_text,
        componentName,
        options: getFilterOptions(type, options),
      },
    ]
  }, [] as MetadataTemplateFieldWithComponentName[])

  return [...metadataFilters, ...systemFilters.value]
})

const numberOfActiveFilters = computed(() => Object.values(props.filters).filter((v) => v).length)

const hasActiveFilters = computed(() =>
  Object.entries(props.filters).some(([k, v]) => {
    if (Array.isArray(v)) {
      return v.length > 0 // Check if the array is not empty
    }

    // For boolean values, consider false as active (explicitly set)
    // * except the ones (system filters) with component CCheckboxFilter
    const componentName = [...computedCustomFilterComponents.value, ...computedFilters.value].find(
      (filter) => filter.name === k,
    )?.componentName
    if (componentName !== 'CCheckboxFilter' && typeof v === 'boolean') {
      return true
    }

    return v !== '' && v !== null && v !== false // Check falsy values for non-array values
  }),
)

// Compute the system-filtered canvas IDs (intersection when multiple filters are active)
const systemFilteredIds = computed<string[] | undefined>(() => {
  const isPopularActive = props.filters[SystemFilter.POPULAR]
  const isEngagingActive = props.filters[SystemFilter.ENGAGING]
  const isRecommendedActive = props.filters[SystemFilter.RECOMMENDED]

  const activeIdArrays: string[][] = []
  if (isPopularActive && props.popularIds?.length) {
    activeIdArrays.push(props.popularIds)
  }
  if (isEngagingActive && props.engagingIds?.length) {
    activeIdArrays.push(props.engagingIds)
  }
  if (isRecommendedActive && props.recommendedIds?.length) {
    activeIdArrays.push(props.recommendedIds)
  }

  // If no system filters are active, return undefined
  if (activeIdArrays.length === 0) {
    return undefined
  }

  // If one filter is active, return its IDs
  if (activeIdArrays.length === 1) {
    return activeIdArrays[0]
  }

  // If multiple filters are active, return the intersection
  const intersection = activeIdArrays[0].filter((id) => activeIdArrays.every((arr) => arr.includes(id)))
  return intersection
})

// Helper function to check if a field belongs to a dependent section
function isFieldFromDependentSection(fieldName: string): boolean {
  if (!template.value?.sections) return false

  return template.value.sections.some(
    (section) => section.are_fields_dependent && section.fields.some((field) => field.name === fieldName),
  )
}

// Helper function to get external filters for a specific field (excludes the field itself)
function getExternalFiltersForField(
  type: MetadataTemplateFieldTypeEnum,
  fieldName: string,
  isDependentField: boolean = false,
) {
  if (['text', 'number'].includes(type) && !isDependentField) {
    return undefined
  }

  const { [fieldName]: _excluded, ...externalFilters } = props.filters

  // Filter out all system filters (they're not metadata fields and backend can't handle them)
  const filteredExternalFilters = Object.entries(externalFilters).reduce(
    (acc, [key, value]) => {
      // Skip all system filters (popular, engaging, recommended, latest)
      if (key.startsWith('system:')) {
        return acc
      }
      acc[key] = value
      return acc
    },
    {} as Record<string, any>,
  )

  return filteredExternalFilters
}

function fetchSuggestions(searchTerm: string) {
  return handleFetch(searchTerm, props.entity, {
    includeNameSearch: true,
    includeDraftValues: props.includeDraftValues,
    isSection: props.isSection,
    isTemplate: props.isTemplate,
    ownerId: props.ownerId,
    searchMode: 'fuzzy',
    threshold: 0.3,
    fetchAllUnique: true,
    contentType: props.contentType,
    tagsNotIn: props.tagsNotIn,
  })
}

function onUpdateModelValue(f: MetadataTemplateField, value: any) {
  if (f.type === MetadataTemplateFieldTypeEnum.CHECKBOX && typeof value === 'string') {
    value = value === 'true'
  }

  return emit(
    'update:filters',
    omitBy(
      {
        ...props.filters,
        [f.name]: value,
      },
      (v) => v === '' || v === null,
    ),
  )
}

interface DependentField {
  name: string
  label: string
  type: 'select' | 'multi-select' | 'checkbox' | 'text' | 'number' | 'date'
  available_values: Array<string | boolean | number>
}

async function fetchDependentFiltersForSection(sectionLabel: string) {
  if (!restApiAxios.value || !originalTemplate.value || !template.value || !pitcherInfo?.value) return

  try {
    const data = await restApiAxios.value.post<unknown, { fields: DependentField[] }>(
      `${pitcherInfo.value.apiOrigin}/core/api/protected/dependent-filters`,
      {
        entity: props.dependentFiltersEntity,
        instance_id: pitcherInfo.value.instanceId,
        section_label: sectionLabel,
        current_filters: props.filters,
      },
    )

    // Update the available options for the section
    if (data?.fields) {
      template.value = {
        ...template.value,
        sections: template.value.sections?.map((section) => {
          if (section.label !== sectionLabel) return section

          // Find the original section to get original options
          const originalSection = originalTemplate.value?.sections?.find((s) => s.label === sectionLabel)
          if (!originalSection) return section

          return {
            ...section,
            fields: section.fields.map((field) => {
              const dependentField = data.fields.find((df) => df.name === field.name)
              // Find original field to get original options
              const originalField = originalSection.fields.find((f) => f.name === field.name)

              if (!dependentField) return field

              if (field.type === 'checkbox') {
                return {
                  ...field,
                  available_values: dependentField.available_values,
                }
              }

              if (['number', 'date', 'text'].includes(field.type)) {
                return {
                  ...field,
                  available_values: dependentField.available_values,
                }
              }

              if (['select', 'multi-select'].includes(field.type) && originalField?.options) {
                const filteredOptions = originalField.options.filter((o) => {
                  const optionValue = typeof o.value === 'string' ? o.value : String(o.value)
                  return dependentField.available_values.some((av) =>
                    typeof av === 'string' ? av === optionValue : String(av) === optionValue,
                  )
                })

                return {
                  ...field,
                  options: filteredOptions,
                }
              }
              return field
            }),
          }
        }),
      }
    }
  } catch (error) {
    console.error(`Failed to fetch dependent filters for section ${sectionLabel}:`, error)
  }
}

// Fetch all dependent filters
async function fetchAllDependentFilters() {
  if (!hasDependentFilters.value) return
  const dependentSections = template.value?.sections?.filter((s) => s.are_fields_dependent) || []
  await Promise.all(dependentSections.map((section) => fetchDependentFiltersForSection(section.label)))
}

function getSectionFieldsToRender(section: any) {
  return section?.fields.filter((f: any) => {
    if (!f.is_filterable) return false

    if (hasDependentFilters.value) {
      if (['select', 'multi-select'].includes(f.type)) {
        return f.options && f.options.length > 0
      }

      if (f.type === 'checkbox') {
        return f.available_values !== undefined && f.available_values.length > 0
      }

      if (['number', 'date', 'text'].includes(f.type)) {
        return f.available_values !== undefined && f.available_values.length > 0
      }
    }

    return true
  })
}

const visibleDependentFields = computed(() => {
  if (!template.value) return []

  const dependentSections = template.value.sections?.filter((s) => s.are_fields_dependent) ?? []
  const allDependentFields = dependentSections.flatMap((section) =>
    section.fields.filter((f: any) => {
      if (!f.is_filterable) return false

      if (['select', 'multi-select'].includes(f.type)) {
        return f.options && f.options.length > 0
      }

      if (f.type === 'checkbox') {
        return f.available_values !== undefined && f.available_values.length > 0
      }

      if (['text', 'number', 'date'].includes(f.type)) {
        return f.available_values !== undefined && f.available_values.length > 0
      }

      return true
    }),
  )

  const lastFilledIndex = allDependentFields.reduce((lastIndex, field, index) => {
    return props.filters[field.name] ? index : lastIndex
  }, -1)

  const lastIndex = lastFilledIndex === -1 ? 1 : Math.min(lastFilledIndex + 2, allDependentFields.length)

  return allDependentFields.slice(0, lastIndex)
})

function onUpdateDependentField(field: MetadataTemplateField, value: any) {
  if (field.type === MetadataTemplateFieldTypeEnum.CHECKBOX && typeof value === 'string') {
    value = value === 'true'
  }

  const allDependentFields =
    (template.value?.sections ?? [])
      .filter((s) => s.are_fields_dependent)
      .flatMap((section) => section.fields.filter((f) => f.is_filterable)) ?? []

  const currentIndex = allDependentFields.findIndex((f) => f.name === field.name)

  // Create new filters object with all subsequent fields cleared
  const newFilters = { ...props.filters }
  allDependentFields.forEach((f, index) => {
    // Only clear fields after the current one
    if (index > currentIndex) {
      delete newFilters[f.name]
    }
  })

  // Update with new value
  if (value === '' || value === null || (Array.isArray(value) && value.length === 0)) {
    delete newFilters[field.name]
  } else {
    newFilters[field.name] = value
  }

  emit('update:filters', newFilters)
}

function clearSearch() {
  searchOnClickRef.value?.clear()
}

function clearAllFiltersAndQuickFilters() {
  // Clear regular filters - CSearchFilter components will automatically detect
  // the change from filled externalFilters to empty {} and refresh their suggestions
  emit('update:filters', {})
}

// Single watcher for filters
watch(
  () => props.filters,
  async () => {
    if (hasDependentFilters.value) {
      const sections = template.value?.sections?.filter((s) => s.are_fields_dependent) ?? []
      for (const section of sections) {
        await fetchDependentFiltersForSection(section.label)
      }
    }
  },
  { deep: true },
)

onMounted(async () => {
  try {
    const response = await metadataFetcher?.()
    const results = response?.results ?? ([] as PaginatedMetadataTemplateList['results'])

    if (!results?.length) {
      console.warn('[CSearchFilters.vue]: No metadata templates found. Bailing...')
      return
    }

    const [templ] = results
    template.value = templ
    originalTemplate.value = cloneDeep(templ)

    expandedNames.value = template.value?.sections?.map((section) => section?.label ?? '') ?? []

    await fetchAllDependentFilters()
  } catch (e) {
    console.error('[CSearchFilters.vue]: Error fetching metadata templates', e)
  }
})
</script>
